/* eslint-disable no-use-before-define */
import React, { useEffect, useState } from 'react';
import _ from 'underscore';
import { titleize } from 'underscore.string';
import CargomaticModal from 'components/v2/modal';
import CargomaticInput from 'components/v2/input';
import ErrorLabel from 'components/v2/forms/ErrorLabel';
import CargomaticSelect from 'components/v2/select';
import moment from 'moment';
import { Translation, useTranslation } from 'react-i18next';

export default function ShipperCostAdjustment({
  selectedLineItem,
  shipment,
  show: showModal,
  equipments,
  onSave,
  onHide,
}) {
  const { t } = useTranslation();
  const [adjustmentData, setAdjustmentData] = useState(() => getInitialValue());
  const [errors, setErrors] = useState({
    note: null,
    reasonCode: null,
    shipperCost: null,
  });

  useEffect(() => {
    setAdjustmentData(() => getInitialValue());
  }, [showModal, selectedLineItem]);

  useEffect(() => {
    runValidationChecks();
  }, [adjustmentData]);

  function getInitialValue() {
    const ledger = 'receivables';
    const shipper_cost = shipment[ledger].filter(
      (adjustment) =>
        selectedLineItem && selectedLineItem._id === adjustment._id,
    )[0];
    return {
      ledger,
      timestamp: shipper_cost?.timestamp || moment(),
      shipperCost: shipper_cost?.value || 0,
      reasonCode: null,
      note: '',
      derivedEquipment: shipper_cost?.equipment || '',
      message: shipper_cost?.message || '',
      _id: shipper_cost?._id || null,
    };
  }

  const REASON_CODES = [
    { value: null, localizationSlug: 'REASON_CODE' },
    { value: 'rate_error', localizationSlug: 'RATE_ERROR' },
    { value: 'fuel_error', localizationSlug: 'FUEL_ERROR' },
    { value: 'btf_error', localizationSlug: 'BTF_ERROR' },
    { value: 'accessorial_error', localizationSlug: 'ACCESSORIAL_ERROR' },
    {
      value: 'customer_accommodation',
      localizationSlug: 'CUSTOMER_ACCOMODATION',
    },
    {
      value: 'missing_documentation',
      localizationSlug: 'MISSING_DOCUMENTATION',
    },
    { value: 'missing_approvals', localizationSlug: 'MISSING_APPROVALS' },
    { value: 'duplicate_invoice', localizationSlug: 'DUPLICATE_INVOICE' },
  ];

  function handleError(type, error) {
    setErrors((prev) => {
      let previous = { ...prev };
      previous[type] = error;
      return previous;
    });
  }

  function runValidationChecks() {
    if (!adjustmentData.shipperCost) {
      handleError('shipperCost', t('EMPTY_SHIPPER_COST_ERROR'));
    } else if (Number(adjustmentData.shipperCost) <= 0) {
      handleError('shipperCost', t('NEGATIVE_SHIPPER_COST_ERROR'));
    } else {
      handleError('shipperCost', null);
    }

    if (!adjustmentData.note?.length) {
      handleError('note', t('EMPTY_NOTES_ERROR'));
    } else {
      handleError('note', null);
    }
    if (!adjustmentData.reasonCode) {
      handleError('reasonCode', t('REQUIRED_REASON_CODE'));
    } else {
      handleError('reasonCode', null);
    }
  }

  function handleSave() {
    let errorsValidated = true;
    const errorsToCheck = Object.keys(errors);

    for (let i = 0; i < errorsToCheck.length; i++) {
      const item = errorsToCheck[i];
      if (errors[item]) {
        errorsValidated = false;
        break;
      }
    }
    if (errorsValidated) onSave(adjustmentData);
  }

  const onChange = (type, value) => {
    setAdjustmentData((previous) => {
      let prev = { ...previous };
      prev[type] = value;
      return prev;
    });
  };

  const getError = (type) => errors[type];

  const getEquipmentLabel = (equipment) => `${equipment.equipment_id}`;

  const renderModalBody = () => {
    return (
      <>
        <div className="flex justify-between">
          <div className="w-52">
            <CargomaticInput
              value={adjustmentData.shipperCost}
              error={errors.shipperCost}
              label={t('ADD_SHIPPER_COST')}
              info={t('ADD_SHIPPER_COST')}
              placeholder={t('NEW_DOLLAR_VALUE_PLACEHOLDER')}
              className="border-l-0"
              type="number"
              onChange={(value) => onChange('shipperCost', value)}
            />
            <ErrorLabel className="mt-1">
              {getError('shipperCost') || ''}
            </ErrorLabel>
          </div>

          <div className="w-2/3">
            <fieldset>
              <label className="block mb-1 form-label">{t('EQUIPMENT')}</label>
              <select
                disabled
                className="form-select"
                value={adjustmentData.derivedEquipment}
              >
                {equipments.map((equipment) => (
                  <option key={equipment._id} value={equipment._id}>
                    {getEquipmentLabel(equipment)}
                  </option>
                ))}
              </select>
            </fieldset>
          </div>
        </div>
        <div className="flex justify-between ">
          <div className="mt-2 flex flex-col justify-between h-20">
            <CargomaticSelect
              label={t('REASON_CODE')}
              className="rounded-md box-content h-5 w-52"
              onSelect={({ value }) => onChange('reasonCode', value)}
              value={adjustmentData.reasonCode}
              placeholder={t('REASON_CODE')}
              items={REASON_CODES}
              valueKey="value"
              textKey="localizationSlug"
            />
            <ErrorLabel className="mt-1">
              {getError('reasonCode') || ''}
            </ErrorLabel>
          </div>
          <div className="w-2/3 mt-1 flex flex-col justify-between">
            <CargomaticInput
              maxLength={100}
              type="text"
              label={t('ENTER_NOTE_LABEL')}
              placeholder={t('ENTER_NOTE_PLACEHOLDER')}
              value={adjustmentData.note}
              onChange={(value) => onChange('note', value)}
            />
            <ErrorLabel className="mt-1">{getError('note') || ''}</ErrorLabel>
          </div>
        </div>
      </>
    );
  };

  const renderFooter = () => {
    return (
      <div className="flex items-center flex-wrap">
        <div className="form-actions">
          <button
            className="mr-2 block button button-green"
            onClick={handleSave}
          >
            <Translation>{(t) => titleize(t('SAVE'))}</Translation>
          </button>

          <button className="button button-secondary" onClick={() => onHide()}>
            <Translation>{(t) => t('CANCEL')}</Translation>
          </button>
        </div>
      </div>
    );
  };

  return (
    <CargomaticModal
      dialogClassName=" md:w-1/2"
      title={t('SHIPPER_COST_ADJUSTMENT')}
      style={{ top: '20%' }}
      show={showModal}
      onHide={() => onHide()}
      backdrop="static"
      body={renderModalBody()}
      footer={renderFooter()}
      location="middle"
    />
  );
}
